import { GymGlyph, Icon, Popover, PopoverHeading, PopoverItem, useToast } from "@helmr/ui";
import { useNavigate } from "react-router-dom";
import { isApiError } from "../api/client";
import { selectSession, type PickerEntry } from "../api/auth";
import { useServices } from "./services";
import { useSessionSnapshot } from "./permissions";
import { strings } from "./strings";
import s from "./shell.module.css";

const t = strings.shell;

/**
 * Gym and branch switcher. Selecting calls `POST /auth/session/select`, stores the new token, then clears the query
 * cache before anything renders for the new context (tenant-leak guard). "All branches" only drops the list filter.
 */
export function Switcher({ variant }: { variant: "top" | "drawer" }) {
  const { client, session, queryClient } = useServices();
  const state = useSessionSnapshot();
  const navigate = useNavigate();
  const toast = useToast();

  const staffEntries = state.picker.filter((e) => e.staff);
  const current = staffEntries.find((e) => e.businessId === state.businessId);
  const branches = current?.staff?.branches ?? [];
  const currentBranch = branches.find((b) => b.id === state.branchId);
  const viewing = state.branchFilter === "all" ? t.allBranches : (branches.find((b) => b.id === state.branchFilter)?.name ?? currentBranch?.name ?? "");
  const others = staffEntries.filter((e) => e.businessId !== state.businessId);

  async function choose(entry: PickerEntry, branchId: string) {
    try {
      const res = await selectSession(client, { businessId: entry.businessId, branchId });
      session.setScoped({ token: res.token, expiresAt: res.expiresAt, businessId: res.businessId, branchId: res.branchId, staffId: res.staffId, roleId: res.roleId });
      // Before the first render of the new context: no row from the old branch or business can flash.
      queryClient.clear();
      if (entry.businessId !== state.businessId) navigate("/home");
    } catch (err) {
      toast.show(isApiError(err) && err.isForbidden ? strings.signIn.errors.noAccess : t.switchFailed);
    }
  }

  return (
    <Popover
      role="listbox"
      label={t.branches}
      align="start"
      minWidth={280}
      className={variant === "drawer" ? s.mgsw : s.brandSwitcher}
      trigger={(p) => (
        <button {...p} className={s.gsw} aria-label={t.switchLabel(viewing || current?.name || "")}>
          <span className={s.logo}><GymGlyph /></span>
          <span className={s.gswText}>{current?.name ?? strings.appName}{viewing ? <small> · {viewing}</small> : null}</span>
          <Icon name="down" className={s.chev} />
        </button>
      )}
    >
      {({ close }) => (
        <>
          <PopoverHeading>{t.branches}</PopoverHeading>
          {branches.map((b) => (
            <PopoverItem key={b.id} role="option" selected={state.branchFilter === b.id} lead={<span className={s.logo}><GymGlyph /></span>}
              onClick={() => { close(); if (current && state.branchFilter !== b.id) void choose(current, b.id); }}>
              {current?.name} · {b.name}
            </PopoverItem>
          ))}
          {branches.length > 1 ? (
            <PopoverItem role="option" selected={state.branchFilter === "all"} lead={<span className={s.logo}><GymGlyph /></span>}
              onClick={() => { close(); session.setBranchFilter("all"); }}>
              {t.allBranches}
            </PopoverItem>
          ) : null}
          {others.length > 0 ? (
            <>
              <PopoverHeading>{t.switchBusiness}</PopoverHeading>
              {others.flatMap((e) => (e.staff?.branches ?? []).map((b) => (
                <PopoverItem key={`${e.businessId}:${b.id}`} role="option" lead={<Icon name="swap" />} onClick={() => { close(); void choose(e, b.id); }}>
                  {e.name} · {b.name}
                </PopoverItem>
              )))}
            </>
          ) : null}
        </>
      )}
    </Popover>
  );
}
